import { PROGRAMS } from "@/data/content";
import { Check, CreditCard, Loader2 } from "lucide-react";
import { useState } from "react";
import { createCheckoutSession } from "@/lib/api";
import { trackEvent } from "@/lib/analytics";
import { toast } from "sonner";

export default function Programs({ onBook }) {
  return (
    <section
      id="programs"
      data-testid="programs-section"
      className="relative bg-ng-bg py-24 md:py-36 border-t border-ng-line hex-bg-strong"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-10 relative z-10">
        <div className="text-center mb-16 max-w-3xl mx-auto">
          <div className="label-eyebrow mb-5">02 · Coaching Programs</div>
          <h2 className="font-display font-bold uppercase text-4xl md:text-6xl tracking-tight text-white leading-[0.95]">
            Choose your{" "}
            <span className="text-ng-blue italic">package</span>.
          </h2>
          <p className="mt-6 text-white/65 font-light text-lg">
            Tailored plans. Real results. Lasting transformation.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 lg:gap-8">
          {PROGRAMS.map((p) => (
            <ProgramCard key={p.id} program={p} onClick={() => onBook(`${p.name} Package`)} />
          ))}
        </div>

        <div className="mt-16 text-center">
          <p className="text-white/50 text-sm font-light">
            Ready to transform?{" "}
            <span className="text-ng-blue font-display font-semibold">
              Choose your plan. Commit to yourself. Let&rsquo;s get to work.
            </span>
          </p>
        </div>
      </div>
    </section>
  );
}

function ProgramCard({ program, onClick }) {
  const featured = program.featured;
  const [paying, setPaying] = useState(false);

  const onPayNow = async () => {
    setPaying(true);
    trackEvent("begin_checkout", {
      currency: "USD",
      value: 299,
      items: [{ item_id: program.id, item_name: program.name, price: 299 }],
    });
    try {
      const { url } = await createCheckoutSession({
        packageId: program.id,
        originUrl: window.location.origin,
      });
      window.location.href = url;
    } catch (err) {
      trackEvent("checkout_error", { package: program.id });
      toast.error(
        err?.response?.data?.detail || "Checkout failed. Try again or contact Coach J."
      );
      setPaying(false);
    }
  };

  const showPayButton = program.id === "foundation";

  return (
    <div
      data-testid={`program-card-${program.id}`}
      className={`relative p-8 md:p-10 transition-all duration-500 flex flex-col h-full bg-ng-surface ${
        featured
          ? "border border-ng-blue/50 shadow-[0_0_80px_-20px_rgba(30,144,255,0.5)]"
          : "border border-ng-line hover:border-ng-blue/30"
      }`}
      style={{ borderTopColor: program.accent, borderTopWidth: 2 }}
    >
      {/* Badge */}
      <div
        className="absolute -top-3 left-1/2 -translate-x-1/2 px-4 py-1 text-[10px] uppercase tracking-[0.3em] font-bold font-display"
        style={{
          backgroundColor: program.accent,
          color: "#fff",
        }}
      >
        {program.badge}
      </div>

      <div className="mt-4 text-center">
        <h3
          className="font-display font-bold uppercase text-3xl md:text-4xl tracking-tight text-white"
          style={{ color: program.accent }}
        >
          {program.name}
        </h3>
        <div className="font-display font-medium uppercase tracking-[0.3em] text-xs text-white/60 mt-1">
          {program.sub}
        </div>
      </div>

      <p className="mt-5 text-white/70 font-light text-sm text-center leading-relaxed">
        {program.summary}
      </p>

      {/* Price box */}
      <div
        className="mt-6 border py-4 text-center"
        style={{ borderColor: program.accent }}
      >
        <span
          className="font-display font-bold text-4xl md:text-5xl"
          style={{ color: program.accent }}
        >
          {program.price}
        </span>
        <span className="text-white/60 text-sm ml-1 uppercase tracking-widest">
          {program.cadence}
        </span>
      </div>

      <div className="mt-6 label-eyebrow" style={{ color: program.accent }}>
        What&rsquo;s Included
      </div>

      <ul className="mt-3 space-y-3 flex-1">
        {program.features.map((f, idx) => {
          const isHeader = f.startsWith("Everything in");
          return (
            <li
              key={idx}
              className={`flex gap-3 text-sm ${
                isHeader
                  ? "font-display font-semibold uppercase tracking-[0.15em] text-xs"
                  : "text-white/80 font-light"
              }`}
              style={isHeader ? { color: program.accent } : {}}
            >
              {!isHeader && (
                <Check
                  size={15}
                  strokeWidth={2}
                  className="mt-0.5 flex-shrink-0"
                  style={{ color: program.accent }}
                />
              )}
              <span>{f}</span>
            </li>
          );
        })}
      </ul>

      <div className="mt-6 pt-5 border-t border-white/10">
        <div className="label-muted mb-2">Best For</div>
        <p className="text-white/65 text-xs font-light leading-relaxed">
          {program.bestFor}
        </p>
      </div>

      <button
        data-testid={`program-cta-${program.id}`}
        onClick={onClick}
        className="mt-8 w-full px-6 py-4 uppercase tracking-[0.22em] text-xs font-bold font-display transition-all text-white"
        style={{
          backgroundColor: program.accent,
          boxShadow: `0 0 30px -8px ${program.accent}`,
        }}
      >
        {program.cta}
      </button>

      {showPayButton && (
        <button
          data-testid={`program-pay-${program.id}`}
          onClick={onPayNow}
          disabled={paying}
          className="mt-3 w-full px-6 py-3 border text-xs uppercase tracking-[0.22em] font-bold font-display transition-all flex items-center justify-center gap-2 hover:bg-white/5 disabled:opacity-60"
          style={{ borderColor: program.accent, color: program.accent }}
        >
          {paying ? (
            <Loader2 size={14} className="animate-spin" />
          ) : (
            <>
              <CreditCard size={14} strokeWidth={2} />
              Pay $299 &amp; Start Now
            </>
          )}
        </button>
      )}
    </div>
  );
}
